import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  getLaneState,
  schedulerTheme,
} from 'pos-client/components/customScheduler/theme/schedulerTheme.ts';
import type {
  Appointment,
  AppointmentClickEvent,
  AppointmentContextMenuEvent,
  AppointmentDblClickEvent,
  AppointmentTooltipShowingEvent,
  AppointmentUpdatedEvent,
  AppointmentUpdatingEvent,
  CellContextMenuEvent,
  CellData,
  SchedulerProps,
  SchedulerRef,
} from 'pos-client/components/customScheduler/types/schedulerTypes';
import { computeLaneAppointmentLayouts } from 'pos-client/components/customScheduler/utils/computeLaneAppointmentLayouts.ts';
import {
  calculateAppointmentDragOffset,
  calculateCurrentTimePosition,
  calculateDropSlot,
  isBeforeCurrentTime as checkIsBeforeCurrentTime,
} from 'pos-client/components/customScheduler/utils/schedulerCalculations.ts';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { useAuthorizeBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useAuthorizeBlockedSlot.ts';
import { useUnauthorizeBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useUnauthorizeBlockSlot.ts';
import { formatLocalISOString } from 'pos-client/components/scheduleBoard/utils/ScheduleBoardUtils.ts';
import { useCurrentUser } from 'pos-client/hooks/useCurrentUser.ts';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';

import ContextMenu from './ContextMenu.tsx';
import ScheduleLane from './ScheduleLane.tsx';
import TimePanel from './TimePanel.tsx';

import './scheduler.css';
import styles from './SchedulerContainer.module.css';

const EMPTY_APPOINTMENTS: Appointment[] = [];

/**
 * SchedulerContainer - Main scheduler component
 *
 * Manages the overall layout and state for the custom scheduler.
 * Provides a day-view calendar interface for managing jobs and blocked slots across lanes.
 *
 * Features:
 * - CSS Grid layout for lanes and time slots
 * - Configurable start/end hours and cell duration
 * - Support for custom cell and appointment rendering
 * - Event handling for interactions (click, drag, context menu)
 * - Virtual scrolling for performance with large datasets
 */
const SchedulerContainer = React.forwardRef<SchedulerRef, SchedulerProps>(
  (
    {
      dataSource,
      currentDate,
      startDayHour,
      endDayHour,
      cellDuration,
      groups,
      height,
      disabled,
      timeZone,
      tradingTimes,
      onAppointmentUpdating,
      onAppointmentUpdated,
      onAppointmentClick,
      onAppointmentDblClick,
      onAppointmentContextMenu,
      onCellContextMenu,
      onAppointmentDragStart,
      onAppointmentAdd,
      onAppointmentTooltipShowing,
      appointmentComponent: AppointmentComponent,
      resourceCellRender,
      dataCellTemplate,
      timeCellTemplate,
      resources,
      defaultLanes = [],
      conflictLanes = [],
      userIsDragging,
      compactView = false,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const timeHeaderRef = useRef<HTMLDivElement>(null);
    const dragOffsetRef = useRef<number | null>(null);

    const { currentUser } = useCurrentUser();

    const {
      setJobDetailsModalData,
      setSelectedOrderDetailsForAssignTechnician,
    } = useScheduleBoardContext();

    const { unauthorizeBlockedSlot } = useUnauthorizeBlockedSlot();
    const { authorizeBlockedSlot } = useAuthorizeBlockedSlot();
    const { hasPermissions } = usePermissions();
    const canAuthorizeBlockedSlot = hasPermissions('authorize.blocked.slot');
    const canAuthorizeOwnBlockedSlot = hasPermissions(
      'authorize.own.blocked.slot'
    );

    const [scrollbarWidth, setScrollbarWidth] = useState(0);

    const [contextMenu, setContextMenu] = useState<{
      visible: boolean;
      x: number;
      y: number;
      items: Array<{ id: string; label: string; action: () => void }>;
    }>({
      visible: false,
      x: 0,
      y: 0,
      items: [],
    });
    const [currentTimePosition, setCurrentTimePosition] = useState<
      number | null
    >(null);
    const [justResized] = useState<string | number | null>(null);

    const [visibleRowRange, setVisibleRowRange] = useState<{
      start: number;
      end: number;
    }>({ start: 0, end: Number.POSITIVE_INFINITY });

    // Validate configuration
    const validatedConfig = useMemo(() => {
      return {
        startDayHour: Math.max(0, Math.min(startDayHour, 23)),
        endDayHour: Math.max(startDayHour + 1, Math.min(endDayHour, 24)),
        cellDuration: Math.max(1, cellDuration),
        groups: groups || [],
        height: height || '100%',
        disabled: disabled || false,
        timeZone: timeZone || 'UTC',
      };
    }, [
      startDayHour,
      endDayHour,
      cellDuration,
      groups,
      height,
      disabled,
      timeZone,
    ]);

    // Calculate number of time slots
    const totalHours =
      validatedConfig.endDayHour - validatedConfig.startDayHour;
    const slotsPerHour = 60 / validatedConfig.cellDuration;
    const totalSlots = totalHours * slotsPerHour;

    const baseCellHeight: number =
      (compactView ? 30 : 60) * (validatedConfig.cellDuration / 30);
    const floorCellHeight = compactView ? 11 : 22;
    const preferredCellHeight = compactView
      ? Math.max(5, baseCellHeight * 0.495)
      : Math.max(floorCellHeight, baseCellHeight);
    const maxCellHeight = Math.max(floorCellHeight, baseCellHeight);

    const [availableHeight, setAvailableHeight] = useState(0);
    useEffect(() => {
      const el = scrollContainerRef.current;
      if (!el) return;

      const measure = () => {
        const next = Math.round(el.clientHeight);
        setAvailableHeight((prev) => (prev === next ? prev : next));
      };

      measure();

      let timeout: ReturnType<typeof setTimeout> | undefined;
      const onResize = () => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(measure, 150);
      };

      window.addEventListener('resize', onResize);
      return () => {
        if (timeout) clearTimeout(timeout);
        window.removeEventListener('resize', onResize);
      };
    }, []);

    const fitCellHeight =
      availableHeight > 0 && totalSlots > 0
        ? availableHeight / totalSlots
        : 0;
    const cellHeightPx = Math.min(
      maxCellHeight,
      Math.max(preferredCellHeight, fitCellHeight)
    );

    // Generate time slots for the day
    const timeSlots = useMemo(() => {
      const slots = [];
      const baseDate = new Date(currentDate);
      baseDate.setHours(validatedConfig.startDayHour, 0, 0, 0);

      for (let i = 0; i < totalSlots; i++) {
        const slotDate = new Date(baseDate);
        slotDate.setMinutes(
          slotDate.getMinutes() + i * validatedConfig.cellDuration
        );
        slots.push(slotDate);
      }

      return slots;
    }, [
      currentDate,
      validatedConfig.startDayHour,
      validatedConfig.cellDuration,
      totalSlots,
    ]);

    // Group appointments by lane
    const appointmentsByLane = useMemo(() => {
      const grouped: { [key: string]: Appointment[] } = {};

      for (const appointment of dataSource) {
        const laneId = String(
          appointment[validatedConfig.groups[0]] || 'default'
        );
        if (!grouped[laneId]) {
          grouped[laneId] = [];
        }
        grouped[laneId].push(appointment);
      }

      return grouped;
    }, [dataSource, validatedConfig.groups]);

    const appointmentLayouts = useMemo(
      () =>
        computeLaneAppointmentLayouts(appointmentsByLane, {
          maxVisibleColumns: 2,
        }),
      [appointmentsByLane]
    );

    // Get unique lanes
    const lanes = useMemo(() => {
      const laneIds = new Set<string>();

      if (resources) {
        for (const resource of resources) {
          laneIds.add(resource.id);
        }
      }

      for (const laneId of Object.keys(appointmentsByLane)) {
        laneIds.add(laneId);
      }

      return Array.from(laneIds);
    }, [resources, appointmentsByLane]);

    // Update current time position
    const updateCurrentTimePosition = useCallback(() => {
      const position = calculateCurrentTimePosition(
        currentDate,
        validatedConfig.startDayHour,
        validatedConfig.endDayHour,
        validatedConfig.cellDuration,
        totalSlots,
        cellHeightPx,
        validatedConfig.timeZone
      );
      setCurrentTimePosition(position);
    }, [currentDate, validatedConfig, totalSlots, cellHeightPx]);

    // Update current time position on mount and every minute
    React.useEffect(() => {
      updateCurrentTimePosition();
      const interval = setInterval(updateCurrentTimePosition, 60000);
      return () => clearInterval(interval);
    }, [updateCurrentTimePosition]);

    React.useEffect(() => {
      const timeHeader = timeHeaderRef.current;
      const scrollContainer = scrollContainerRef.current;

      if (!timeHeader || !scrollContainer) return;

      // Calculate scrollbar width and apply padding to timeHeader
      const measuredScrollbarWidth =
        scrollContainer.offsetWidth - scrollContainer.clientWidth;

      setScrollbarWidth(measuredScrollbarWidth);

      if (measuredScrollbarWidth > 0) {
        timeHeader.style.paddingRight = `${measuredScrollbarWidth}px`;
      }

      const OVERSCAN = 6;
      const recomputeVisibleRange = () => {
        const scrollTop = scrollContainer.scrollTop;
        const clientHeight = scrollContainer.clientHeight;
        if (clientHeight === 0 || cellHeightPx === 0) return;

        const start = Math.max(
          0,
          Math.floor(scrollTop / cellHeightPx) - OVERSCAN
        );
        const end = Math.min(
          totalSlots,
          Math.ceil((scrollTop + clientHeight) / cellHeightPx) + OVERSCAN
        );

        setVisibleRowRange((prev) =>
          prev.start === start && prev.end === end ? prev : { start, end }
        );
      };

      const handleContainerScroll = () => {
        if (timeHeader.scrollLeft !== scrollContainer.scrollLeft) {
          timeHeader.scrollLeft = scrollContainer.scrollLeft;
        }
        recomputeVisibleRange();
      };

      scrollContainer.addEventListener('scroll', handleContainerScroll, {
        passive: true,
      });

      recomputeVisibleRange();
      const resizeObserver =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(() => recomputeVisibleRange())
          : null;
      if (resizeObserver) resizeObserver.observe(scrollContainer);

      return () => {
        scrollContainer.removeEventListener('scroll', handleContainerScroll);
        timeHeader.style.paddingRight = '0px';
        resizeObserver?.disconnect();
      };
    }, [cellHeightPx, totalSlots]);

    // Move job scroll functionality
    useEffect(() => {
      const verticalScrollContainer = scrollContainerRef.current;
      const horizontalScrollContainer = containerRef.current;
      if (!verticalScrollContainer || !horizontalScrollContainer) return;

      const EDGE_THRESHOLD = 60;
      const MAX_SCROLL_SPEED = 22;

      let rafId: number | null = null;
      let scrollDeltaX = 0;
      let scrollDeltaY = 0;

      const step = () => {
        rafId = null;
        if (scrollDeltaY !== 0)
          verticalScrollContainer.scrollTop += scrollDeltaY;
        if (scrollDeltaX !== 0)
          horizontalScrollContainer.scrollLeft += scrollDeltaX;
        if (scrollDeltaX !== 0 || scrollDeltaY !== 0) {
          rafId = requestAnimationFrame(step);
        }
      };

      const computeDelta = (distance: number): number => {
        if (distance >= EDGE_THRESHOLD || distance < 0) return 0;
        const ratio = (EDGE_THRESHOLD - distance) / EDGE_THRESHOLD;
        return Math.max(1, Math.ceil(ratio * MAX_SCROLL_SPEED));
      };

      const handleDragOver = (e: DragEvent) => {
        const vRect = verticalScrollContainer.getBoundingClientRect();
        const hRect = horizontalScrollContainer.getBoundingClientRect();
        const { clientX: x, clientY: y } = e;

        const insideY = y >= vRect.top && y <= vRect.bottom;
        const insideX = x >= hRect.left && x <= hRect.right;

        scrollDeltaX = 0;
        scrollDeltaY = 0;

        if (insideY && insideX) {
          const viewTop = Math.max(vRect.top, 0);
          const viewBottom = Math.min(vRect.bottom, window.innerHeight);
          const viewLeft = Math.max(hRect.left, 0);
          const viewRight = Math.min(hRect.right, window.innerWidth);

          const topDist = y - viewTop;
          const bottomDist = viewBottom - y;
          const leftDist = x - viewLeft;
          const rightDist = viewRight - x;

          if (topDist < EDGE_THRESHOLD) {
            scrollDeltaY = -computeDelta(topDist);
          } else if (bottomDist < EDGE_THRESHOLD) {
            scrollDeltaY = computeDelta(bottomDist);
          }

          if (leftDist < EDGE_THRESHOLD) {
            scrollDeltaX = -computeDelta(leftDist);
          } else if (rightDist < EDGE_THRESHOLD) {
            scrollDeltaX = computeDelta(rightDist);
          }
        }

        if ((scrollDeltaX !== 0 || scrollDeltaY !== 0) && rafId === null) {
          rafId = requestAnimationFrame(step);
        }
      };

      const stopScrolling = () => {
        scrollDeltaX = 0;
        scrollDeltaY = 0;
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      };

      const handleDragLeave = (e: DragEvent) => {
        if (e.target === horizontalScrollContainer) stopScrolling();
      };

      // When the cursor leaves the browser window during a drag, the container
      // stops receiving dragover events but our RAF loop keeps scrolling in the
      // last direction. Detect window-exit and stop.
      const handleWindowDragLeave = (e: DragEvent) => {
        // relatedTarget is null when the drag leaves the window entirely.
        // Also guard by checking coordinates against the viewport.
        const leftWindow =
          e.relatedTarget === null ||
          e.clientX <= 0 ||
          e.clientY <= 0 ||
          e.clientX >= window.innerWidth ||
          e.clientY >= window.innerHeight;
        if (leftWindow) stopScrolling();
      };

      horizontalScrollContainer.addEventListener('dragover', handleDragOver);
      horizontalScrollContainer.addEventListener('drop', stopScrolling);
      horizontalScrollContainer.addEventListener('dragleave', handleDragLeave);
      document.addEventListener('dragleave', handleWindowDragLeave);
      window.addEventListener('dragend', stopScrolling);
      window.addEventListener('blur', stopScrolling);
      window.addEventListener('mouseout', stopScrolling);

      return () => {
        horizontalScrollContainer.removeEventListener(
          'dragover',
          handleDragOver
        );
        horizontalScrollContainer.removeEventListener('drop', stopScrolling);
        horizontalScrollContainer.removeEventListener(
          'dragleave',
          handleDragLeave
        );
        document.removeEventListener('dragleave', handleWindowDragLeave);
        window.removeEventListener('dragend', stopScrolling);
        window.removeEventListener('blur', stopScrolling);
        window.removeEventListener('mouseout', stopScrolling);
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }, []);

    // Check if a time slot is before the current time
    const isBeforeCurrentTime = useCallback(
      (slot: Date): boolean => {
        return checkIsBeforeCurrentTime(slot, validatedConfig.timeZone);
      },
      [validatedConfig.timeZone]
    );

    // Handle context menu close
    const handleContextMenuClose = useCallback(() => {
      setContextMenu({
        visible: false,
        x: 0,
        y: 0,
        items: [],
      });
    }, []);

    // Wrapper functions to adapt event types for child components
    const handleCellContextMenuWrapper = useCallback(
      (data: {
        cellData: unknown;
        cellElement: HTMLDivElement;
        event: MouseEvent;
        action: string;
      }) => {
        if (onCellContextMenu) {
          const event: CellContextMenuEvent = {
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            cellData: data.cellData as CellData,
            cellElement: data.cellElement,
            event: data.event,
            action: data.action as 'add-order' | 'add-blocked-slot' | 'add-job',
          };
          onCellContextMenu(event);
        }
      },
      [onCellContextMenu]
    );

    const handleAppointmentClickWrapper = useCallback(
      (data: {
        appointmentData: Appointment;
        appointmentElement: HTMLDivElement;
      }) => {
        if (onAppointmentClick) {
          const event: AppointmentClickEvent = {
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            appointmentData: data.appointmentData,
            appointmentElement: data.appointmentElement,
          };
          onAppointmentClick(event);
        }
      },
      [onAppointmentClick]
    );

    const handleAppointmentDblClickWrapper = useCallback(
      (data: {
        appointmentData: Appointment;
        appointmentElement: HTMLDivElement;
      }) => {
        if (onAppointmentDblClick) {
          const event: AppointmentDblClickEvent = {
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            appointmentData: data.appointmentData,
            appointmentElement: data.appointmentElement,
          };
          onAppointmentDblClick(event);
        }
      },
      [onAppointmentDblClick]
    );

    const handleAppointmentContextMenuWrapper = useCallback(
      (data: {
        appointmentData: Appointment;
        appointmentElement: HTMLDivElement;
        event: MouseEvent;
      }) => {
        if (onAppointmentContextMenu) {
          const event: AppointmentContextMenuEvent = {
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            appointmentData: data.appointmentData,
            appointmentElement: data.appointmentElement,
            event: data.event,
          };
          onAppointmentContextMenu(event);
        }
      },
      [onAppointmentContextMenu]
    );

    const handleAppointmentTooltipShowingWrapper = useCallback(
      (data: { appointmentData: Appointment; cancel: boolean }) => {
        if (onAppointmentTooltipShowing) {
          const event: AppointmentTooltipShowingEvent = {
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            appointmentData: data.appointmentData,
            cancel: data.cancel,
          };
          onAppointmentTooltipShowing(event);
        }
      },
      [onAppointmentTooltipShowing]
    );

    const handleAppointmentUpdatingWrapper = useCallback(
      (data: {
        appointmentData: Appointment;
        newData: Partial<Appointment>;
        cancel: boolean;
      }) => {
        if (onAppointmentUpdating) {
          const event: AppointmentUpdatingEvent = {
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            appointmentData: data.appointmentData,
            newData: data.newData,
            cancel: data.cancel,
          };
          onAppointmentUpdating(event);
        }
      },
      [onAppointmentUpdating]
    );

    const handleAppointmentUpdatedWrapper = useCallback(
      (data: { appointmentData: Appointment }) => {
        if (onAppointmentUpdated) {
          const event: AppointmentUpdatedEvent = {
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            appointmentData: data.appointmentData,
          };
          onAppointmentUpdated(event);
        }
      },
      [onAppointmentUpdated]
    );

    // Handle drag start event
    const handleAppointmentDragStart = useCallback(
      (e: React.DragEvent<HTMLDivElement>, appointment: Appointment) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/json', JSON.stringify(appointment));

        if (scrollContainerRef.current) {
          const offset = calculateAppointmentDragOffset(
            e,
            scrollContainerRef.current
          );
          dragOffsetRef.current = offset;
        }

        if (onAppointmentDragStart) {
          onAppointmentDragStart({
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            itemData: appointment,
            itemElement: e.currentTarget,
            cancel: false,
          });
        }
      },
      [onAppointmentDragStart]
    );

    // Handle drag over event
    const handleAppointmentDragOver = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
      },
      []
    );

    // Format slot time correctly accounting for timezone context
    const formatSlotTime = useCallback(
      (slotDate: Date): string => {
        const baseDate = new Date(currentDate);
        baseDate.setHours(validatedConfig.startDayHour, 0, 0, 0);

        const minutesElapsed =
          (slotDate.getTime() - baseDate.getTime()) / (1000 * 60);

        const displayDate = new Date(currentDate);
        displayDate.setHours(validatedConfig.startDayHour, 0, 0, 0);
        displayDate.setMinutes(displayDate.getMinutes() + minutesElapsed);

        return formatLocalISOString(displayDate);
      },
      [currentDate, validatedConfig.startDayHour]
    );

    const handleCellDrop = useCallback(
      (
        e: React.DragEvent<HTMLDivElement>,
        _cellData: unknown,
        laneId: string,
        slot: Date
      ) => {
        e.preventDefault();
        e.stopPropagation();

        try {
          const draggedData = e.dataTransfer.getData('application/json');
          if (!draggedData) {
            return;
          }

          const draggedAppointment = JSON.parse(draggedData) as Appointment;

          const startDateVal =
            typeof draggedAppointment.startDateDisplay === 'string'
              ? draggedAppointment.startDateDisplay
              : draggedAppointment.startDate;
          const endDateVal =
            typeof draggedAppointment.endDateDisplay === 'string'
              ? draggedAppointment.endDateDisplay
              : draggedAppointment.endDate;
          const originalStart = new Date(startDateVal);
          const originalEnd = new Date(endDateVal);
          const durationMs = originalEnd.getTime() - originalStart.getTime();

          let dropSlot = slot;

          if (dragOffsetRef.current !== null && scrollContainerRef.current) {
            dropSlot = calculateDropSlot(
              e,
              slot,
              dragOffsetRef.current,
              scrollContainerRef.current,
              cellHeightPx,
              timeSlots
            );
          }

          dragOffsetRef.current = null;

          const startDateDisplay = formatSlotTime(dropSlot);
          const newEndDate = new Date(dropSlot.getTime() + durationMs);
          const endDateDisplay = formatSlotTime(newEndDate);

          const updatedAppointment: Appointment = {
            ...draggedAppointment,
            startDate: startDateDisplay,
            endDate: endDateDisplay,
            [validatedConfig.groups[0] || 'laneId']: laneId,
          };

          const isInternalMove = dataSource.some(
            (apt) => apt.id === draggedAppointment.id
          );

          if (isInternalMove) {
            if (onAppointmentUpdating) {
              onAppointmentUpdating({
                component:
                  SchedulerContainer as React.ComponentType<SchedulerProps>,
                appointmentData: draggedAppointment,
                newData: {
                  startDate: startDateDisplay,
                  endDate: endDateDisplay,
                  [validatedConfig.groups[0] || 'laneId']: laneId,
                },
                cancel: false,
              });
            }

            if (onAppointmentUpdated) {
              onAppointmentUpdated({
                component:
                  SchedulerContainer as React.ComponentType<SchedulerProps>,
                appointmentData: updatedAppointment,
              });
            }
          } else {
            if (onAppointmentAdd) {
              onAppointmentAdd({
                component:
                  SchedulerContainer as React.ComponentType<SchedulerProps>,
                fromComponent:
                  SchedulerContainer as React.ComponentType<SchedulerProps>,
                fromData: draggedAppointment,
                itemData: updatedAppointment,
                toComponent:
                  SchedulerContainer as React.ComponentType<SchedulerProps>,
                toData: updatedAppointment,
              });
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      },
      [
        validatedConfig.groups,
        dataSource,
        onAppointmentUpdating,
        onAppointmentUpdated,
        onAppointmentAdd,
        formatSlotTime,
        timeSlots,
        cellHeightPx,
      ]
    );

    // Handle drag end event
    const handleAppointmentDragEnd = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();
      },
      []
    );

    // Handle appointment deletion
    const handleAppointmentDelete = useCallback(
      (appointment: Appointment) => {
        if (onAppointmentUpdated) {
          onAppointmentUpdated({
            component:
              SchedulerContainer as React.ComponentType<SchedulerProps>,
            appointmentData: appointment,
          });
        }
      },
      [onAppointmentUpdated]
    );

    // Expose scrollToDate method via ref
    React.useImperativeHandle(
      ref,
      () => ({
        scrollToDate: (date: Date) => {
          if (!scrollContainerRef.current) return;

          const dayStart = new Date(currentDate);
          dayStart.setHours(validatedConfig.startDayHour, 0, 0, 0);

          const targetDate = new Date(date);
          const minutesFromStart =
            (targetDate.getTime() - dayStart.getTime()) / (1000 * 60);

          const pixelsPerMinute =
            (totalSlots * cellHeightPx) /
            (totalSlots * validatedConfig.cellDuration);
          const position = minutesFromStart * pixelsPerMinute;

          const containerHeight = scrollContainerRef.current.clientHeight;
          const scrollToPosition = Math.max(0, position - containerHeight / 2);

          scrollContainerRef.current.scrollTop = scrollToPosition;
        },
      }),
      [
        currentDate,
        validatedConfig.startDayHour,
        validatedConfig.cellDuration,
        totalSlots,
        cellHeightPx,
      ]
    );

    return (
      <div
        ref={containerRef}
        className={styles.container}
        style={{
          height: validatedConfig.height,
          opacity: validatedConfig.disabled ? 0.6 : 1,
          pointerEvents: validatedConfig.disabled ? 'none' : 'auto',
        }}
      >
        <div
          style={{
            minWidth: `${80 + lanes.length * 300 + scrollbarWidth}px`,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Header with time and resource info */}
          <div className={styles.header}>
            <div className={styles.resourceHeader} />

            <div ref={timeHeaderRef} className={styles.timeHeader}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${lanes.length}, minmax(300px, 1fr))`,
                  width: '100%',
                }}
              >
                {lanes.map((laneId) => {
                  const hasConflict = conflictLanes.includes(laneId);
                  const isDefaultLane = defaultLanes.includes(laneId);
                  const laneState = getLaneState(hasConflict, isDefaultLane);
                  const laneColors = schedulerTheme.laneStates[laneState];

                  let borderColor = '';
                  let borderStyle = '';
                  let borderWidth = '';

                  if (hasConflict) {
                    borderColor = schedulerTheme.laneStates.conflict.border;
                    borderStyle = 'solid';
                    borderWidth = '2px';
                  }
                  if (hasConflict && isDefaultLane) {
                    borderColor =
                      schedulerTheme.laneStates.conflictDefault.border;
                    borderStyle = 'solid';
                  }

                  return (
                    <div
                      key={`header-${laneId}`}
                      className={styles.laneHeader}
                      data-lane-state={laneState}
                      style={{
                        borderColor,
                        borderStyle,
                        backgroundColor: laneColors.header,
                        borderWidth,
                      }}
                    >
                      {resourceCellRender ? (
                        resourceCellRender({
                          data: resources?.find((r) => r.id === laneId) || {
                            id: laneId,
                          },
                          id: laneId,
                          text: String(
                            resources?.find((r) => r.id === laneId)?.name ?? ''
                          ),
                        })
                      ) : (
                        <span>{laneId}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main scheduler grid with scrollable time panel */}
          <div ref={scrollContainerRef} className={styles.scrollContainer}>
            <TimePanel
              startDayHour={validatedConfig.startDayHour}
              endDayHour={validatedConfig.endDayHour}
              cellDuration={validatedConfig.cellDuration}
              cellHeightPx={cellHeightPx}
              timeZone={validatedConfig.timeZone}
              timeCellTemplate={timeCellTemplate}
              currentDate={currentDate}
              currentTimePosition={currentTimePosition}
              currentTimeColor={schedulerTheme.ui.currentTimeIndicator}
            />

            <div
              className={styles.grid}
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                height: `${timeSlots.length * cellHeightPx}px`,
                position: 'relative',
              }}
            >
              {lanes.map((laneId) => (
                <ScheduleLane
                  key={laneId}
                  laneId={laneId}
                  timeSlots={timeSlots}
                  appointments={
                    appointmentsByLane[laneId] ?? EMPTY_APPOINTMENTS
                  }
                  cellHeightPx={cellHeightPx}
                  cellDuration={cellDuration}
                  conflictLanes={conflictLanes}
                  defaultLanes={defaultLanes}
                  tradingTimes={tradingTimes}
                  validatedConfig={validatedConfig}
                  appointmentLayouts={appointmentLayouts}
                  isBeforeCurrentTime={isBeforeCurrentTime}
                  visibleRowStart={visibleRowRange.start}
                  visibleRowEnd={visibleRowRange.end}
                  currentTimePosition={currentTimePosition}
                  currentTimeColor={schedulerTheme.ui.currentTimeIndicator}
                  onCellContextMenu={handleCellContextMenuWrapper}
                  onAppointmentClick={handleAppointmentClickWrapper}
                  onAppointmentDblClick={handleAppointmentDblClickWrapper}
                  onAppointmentContextMenu={handleAppointmentContextMenuWrapper}
                  onAppointmentTooltipShowing={
                    handleAppointmentTooltipShowingWrapper
                  }
                  onAppointmentDragStart={handleAppointmentDragStart}
                  onAppointmentDragOver={handleAppointmentDragOver}
                  onAppointmentDragEnd={handleAppointmentDragEnd}
                  onAppointmentUpdating={handleAppointmentUpdatingWrapper}
                  onAppointmentUpdated={handleAppointmentUpdatedWrapper}
                  handleCellDrop={handleCellDrop}
                  handleAppointmentDelete={handleAppointmentDelete}
                  AppointmentComponent={AppointmentComponent}
                  dataCellTemplate={dataCellTemplate}
                  formatSlotTime={formatSlotTime}
                  justResized={justResized}
                  setContextMenu={setContextMenu}
                  t={t as (key: string) => string}
                  setJobDetailsModalData={
                    setJobDetailsModalData as (
                      data: Record<string, unknown>
                    ) => void
                  }
                  setSelectedOrderDetailsForAssignTechnician={
                    setSelectedOrderDetailsForAssignTechnician as (data: {
                      orderId: string;
                      jobId: string;
                    }) => void
                  }
                  canAuthorizeBlockedSlot={canAuthorizeBlockedSlot}
                  canAuthorizeOwnBlockedSlot={canAuthorizeOwnBlockedSlot}
                  currentUserId={currentUser?.id}
                  unauthorizeBlockedSlot={unauthorizeBlockedSlot}
                  authorizeBlockedSlot={authorizeBlockedSlot}
                  userIsDragging={userIsDragging}
                />
              ))}
            </div>
          </div>

          <ContextMenu
            visible={contextMenu.visible}
            x={contextMenu.x}
            y={contextMenu.y}
            items={contextMenu.items}
            onClose={handleContextMenuClose}
          />
        </div>
      </div>
    );
  }
);

SchedulerContainer.displayName = 'SchedulerContainer';

export default SchedulerContainer;
